import { useState, useEffect } from 'react';
import { Modal, Spinner } from 'react-bootstrap';
import { IconCheck, IconPlus, IconTrash, IconClock, IconAlertCircle, IconBriefcase, IconRobot, IconHelpCircle, IconPencil, IconX } from '@tabler/icons-react';
import ServiceAgentVisibilityGuideModal from './ServiceAgentVisibilityGuideModal';
import '../../styles/WorkingHoursModal.css';

const DAYS = [
  { value: 0, label: 'Segunda-feira' },
  { value: 1, label: 'Terça-feira' },
  { value: 2, label: 'Quarta-feira' },
  { value: 3, label: 'Quinta-feira' },
  { value: 4, label: 'Sexta-feira' },
  { value: 5, label: 'Sábado' },
  { value: 6, label: 'Domingo' },
];

const makeDefault = () =>
  DAYS.map((d) => ({
    day_of_week: d.value,
    is_active: d.value < 5,
    intervals: d.value < 5 ? [{ start_time: '08:00', end_time: '18:00' }] : [],
  }));

const normalize = (value) => {
  if (!value || !Array.isArray(value) || value.length === 0) return makeDefault();
  const map = {};
  value.forEach((d) => { map[d.day_of_week] = d; });
  return DAYS.map(({ value: dayVal }) =>
    map[dayVal] !== undefined ? map[dayVal] : { day_of_week: dayVal, is_active: false, intervals: [] }
  );
};

/**
 * Returns a status chip descriptor for the working hours configuration.
 * { label: string, active: bool }
 */
export const getHoursChip = (hours) => {
  if (!hours || !Array.isArray(hours) || hours.length === 0)
    return { label: 'Usando padrão da conta', active: false };
  const activeDays = hours.filter((d) => d.is_active);
  if (activeDays.length === 0)
    return { label: 'Usando padrão da conta', active: false };
  const isSimpleMF =
    activeDays.length === 5 &&
    activeDays.every(
      (d) =>
        d.day_of_week < 5 &&
        d.intervals.length === 1 &&
        d.intervals[0].start_time === '08:00' &&
        d.intervals[0].end_time === '18:00'
    );
  if (isSimpleMF) return { label: 'Seg–Sex, 08:00–18:00', active: true };
  return { label: `Configurado (${activeDays.length} dia${activeDays.length > 1 ? 's' : ''})`, active: true };
};

/**
 * Returns a status chip descriptor for the services list.
 * { label: string, active: bool }
 */
export const getServicesChip = (services) => {
  if (!services || services.length === 0)
    return { label: 'Nenhum serviço', active: false };
  return { label: `${services.length} serviço${services.length > 1 ? 's' : ''}`, active: true };
};

/**
 * WorkingHoursModal — "Configurar agenda"
 *
 * Props:
 *   show:                     bool
 *   onClose:                  () => void
 *   onSave:                   async (hours, services, agentSlotDuration, agentSlotEnabled) => void
 *                             — throws on error; modal closes on success
 *                             — agentSlotDuration is number | null (null = use global)
 *                             — agentSlotEnabled is bool (false = modo livre, usa duração do serviço)
 *   calendarName:             string
 *   initialValue:             array | null   — working hours
 *   initialServices:          array | null   — [{ id, name, duration, price, showInAgent }]
 *                                              id is null for new services added in modal
 *                                              showInAgent defaults to true
 *   showAgentToggle:          bool           — show per-service "visible to agent" toggle
 *                                              (only applies when services persist as AppointmentType)
 *   initialAgentSlotDuration: number | null  — current slot for this agenda (null = uses global)
 *   initialAgentSlotEnabled:  bool           — current state of the slot switch (default true)
 *   globalSlotDuration:       number         — global BusinessHoursSettings.appointment_duration
 *                                              (used as placeholder when local is empty)
 */
const WorkingHoursModal = ({ show, onClose, onSave, calendarName, initialValue, initialServices, showAgentToggle = false, initialAgentSlotDuration = null, initialAgentSlotEnabled = true, globalSlotDuration = 30 }) => {
  const [days, setDays] = useState(makeDefault);
  const [services, setServices] = useState([]);
  const [newName, setNewName] = useState('');
  const [newDuration, setNewDuration] = useState(30);
  const [newPrice, setNewPrice] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [showGuide, setShowGuide] = useState(false);
  const [agentSlotDuration, setAgentSlotDuration] = useState('');
  const [agentSlotEnabled, setAgentSlotEnabled] = useState(true);
  const [editingIdx, setEditingIdx] = useState(null);
  const [editDraft, setEditDraft] = useState(null);

  useEffect(() => {
    if (show) {
      setDays(normalize(initialValue));
      setServices(initialServices || []);
      setNewName('');
      setNewDuration(30);
      setNewPrice('');
      setSaveError(null);
      setSaving(false);
      setAgentSlotDuration(
        initialAgentSlotDuration != null ? String(initialAgentSlotDuration) : ''
      );
      setAgentSlotEnabled(initialAgentSlotEnabled !== false);
      setEditingIdx(null);
      setEditDraft(null);
    }
  }, [show, initialValue, initialServices, initialAgentSlotDuration, initialAgentSlotEnabled]);

  // -- Hours handlers --
  const updateDay = (dayVal, patch) =>
    setDays((prev) => prev.map((d) => (d.day_of_week === dayVal ? { ...d, ...patch } : d)));

  const addInterval = (dayVal) => {
    const day = days.find((d) => d.day_of_week === dayVal);
    updateDay(dayVal, {
      intervals: [...(day?.intervals || []), { start_time: '08:00', end_time: '18:00' }],
      is_active: true,
    });
  };

  const removeInterval = (dayVal, ivIdx) => {
    const day = days.find((d) => d.day_of_week === dayVal);
    const intervals = (day?.intervals || []).filter((_, i) => i !== ivIdx);
    updateDay(dayVal, { intervals, is_active: intervals.length > 0 });
  };

  const updateInterval = (dayVal, ivIdx, field, val) => {
    const day = days.find((d) => d.day_of_week === dayVal);
    const intervals = (day?.intervals || []).map((iv, i) => (i === ivIdx ? { ...iv, [field]: val } : iv));
    updateDay(dayVal, { intervals });
  };

  // -- Services handlers --
  const addService = () => {
    if (!newName.trim()) return;
    setServices((prev) => [
      ...prev,
      { id: null, name: newName.trim(), duration: parseInt(newDuration) || 30, price: newPrice ? parseFloat(newPrice) : null, showInAgent: true },
    ]);
    setNewName('');
    setNewDuration(30);
    setNewPrice('');
  };

  const removeService = (idx) => setServices((prev) => prev.filter((_, i) => i !== idx));

  const toggleServiceAgentVisibility = (idx) =>
    setServices((prev) => prev.map((s, i) => (i === idx ? { ...s, showInAgent: s.showInAgent === false ? true : false } : s)));

  const startEditService = (idx) => {
    const s = services[idx];
    setEditingIdx(idx);
    setEditDraft({
      name: s.name || '',
      duration: String(s.duration || 30),
      price: s.price != null && s.price !== '' ? String(s.price) : '',
    });
  };

  const cancelEditService = () => {
    setEditingIdx(null);
    setEditDraft(null);
  };

  const confirmEditService = () => {
    if (editingIdx == null || !editDraft) return;
    const cleanName = editDraft.name.trim();
    if (!cleanName) return; // não confirma com nome vazio
    const cleanDuration = Math.max(1, parseInt(editDraft.duration, 10) || 30);
    const cleanPrice = editDraft.price === '' ? null : parseFloat(editDraft.price) || 0;
    setServices((prev) => prev.map((s, i) => (
      i === editingIdx
        ? { ...s, name: cleanName, duration: cleanDuration, price: cleanPrice }
        : s
    )));
    setEditingIdx(null);
    setEditDraft(null);
  };

  const updateEditDraft = (field, value) =>
    setEditDraft((prev) => ({ ...prev, [field]: value }));

  // -- Save --
  const handleSave = async () => {
    setSaving(true);
    setSaveError(null);
    try {
      const parsedSlot = agentSlotDuration.trim() === ''
        ? null
        : Math.max(1, parseInt(agentSlotDuration, 10) || 0) || null;
      await onSave(days, services, parsedSlot, agentSlotEnabled);
      onClose();
    } catch (err) {
      setSaveError(err?.response?.data?.error || err?.message || 'Erro ao salvar');
      setSaving(false);
    }
  };

  return (
    <>
    <Modal show={show} onHide={onClose} centered size="lg">
      <Modal.Header closeButton className="whm-header">
        <div className="whm-header-title">
          <IconBriefcase size={16} style={{ color: '#10b981' }} />
          <span className="whm-header-text">
            Configurar agenda{calendarName ? ` — ${calendarName}` : ''}
          </span>
          {showAgentToggle && (
            <button
              type="button"
              onClick={() => setShowGuide(true)}
              className="whm-guide-btn"
              title="Guia sobre visibilidade dos serviços para o agente"
            >
              <IconHelpCircle size={14} /> Guia
            </button>
          )}
        </div>
      </Modal.Header>

      <Modal.Body className="whm-body">

        {/* ── Serviços ── */}
        <div className="whm-section-title">
          <IconBriefcase size={14} style={{ color: '#10b981' }} />
          Serviços
        </div>

        {services.length === 0 && (
          <p className="whm-service-empty">Nenhum serviço configurado.</p>
        )}

        {services.map((svc, idx) => {
          const visibleToAgent = svc.showInAgent !== false;
          const isEditing = editingIdx === idx;
          return (
            <div
              key={idx}
              className={`whm-service-row${!visibleToAgent ? ' whm-service-row--muted' : ''}${isEditing ? ' whm-service-row--editing' : ''}`}
            >
              {isEditing ? (
                <>
                  <input
                    type="text"
                    value={editDraft.name}
                    onChange={(e) => updateEditDraft('name', e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') confirmEditService();
                      else if (e.key === 'Escape') cancelEditService();
                    }}
                    placeholder="Nome"
                    className="whm-mini-input whm-service-edit-name"
                    autoFocus
                  />
                  <input
                    type="number"
                    min="1"
                    step="5"
                    value={editDraft.duration}
                    onChange={(e) => updateEditDraft('duration', e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') confirmEditService();
                      else if (e.key === 'Escape') cancelEditService();
                    }}
                    className="whm-mini-input whm-service-edit-duration"
                  />
                  <span className="whm-service-meta">min</span>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={editDraft.price}
                    onChange={(e) => updateEditDraft('price', e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') confirmEditService();
                      else if (e.key === 'Escape') cancelEditService();
                    }}
                    placeholder="0.00"
                    className="whm-mini-input whm-service-edit-price"
                  />
                  <button
                    type="button"
                    onClick={confirmEditService}
                    className="whm-icon-btn whm-icon-btn--success"
                    title="Confirmar"
                    disabled={!editDraft.name.trim()}
                  >
                    <IconCheck size={14} />
                  </button>
                  <button
                    type="button"
                    onClick={cancelEditService}
                    className="whm-icon-btn"
                    title="Cancelar"
                  >
                    <IconX size={14} />
                  </button>
                </>
              ) : (
                <>
                  <span className="whm-service-name">{svc.name}</span>
                  <span className="whm-service-meta">{svc.duration} min</span>
                  {svc.price ? (
                    <span className="whm-service-meta">R$ {parseFloat(svc.price).toFixed(2)}</span>
                  ) : null}
                  {showAgentToggle && (
                    <label
                      className="whm-toggle"
                      title={visibleToAgent
                        ? 'Visível para o agente — clique para ocultar do prompt'
                        : 'Oculto do agente — só aparece na agenda'}
                    >
                      <input
                        type="checkbox"
                        checked={visibleToAgent}
                        onChange={() => toggleServiceAgentVisibility(idx)}
                      />
                      <span className="whm-toggle-slider" />
                    </label>
                  )}
                  {showAgentToggle && (
                    <IconRobot
                      size={14}
                      style={{ color: visibleToAgent ? '#10b981' : '#94a3b8' }}
                    />
                  )}
                  <button
                    type="button"
                    onClick={() => startEditService(idx)}
                    className="whm-icon-btn"
                    title="Editar serviço"
                  >
                    <IconPencil size={13} />
                  </button>
                  <button
                    type="button"
                    onClick={() => removeService(idx)}
                    className="whm-icon-btn whm-icon-btn--danger"
                    title="Remover serviço"
                  >
                    <IconTrash size={13} />
                  </button>
                </>
              )}
            </div>
          );
        })}

        {/* Add service form */}
        <div className="whm-add-service-form">
          <div className="whm-form-field--name">
            <label className="whm-form-label">Nome</label>
            <input
              type="text"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && addService()}
              placeholder="Ex: Consulta"
              className="whm-mini-input"
            />
          </div>
          <div className="whm-form-field--duration">
            <label className="whm-form-label">Min</label>
            <input
              type="number"
              value={newDuration}
              onChange={(e) => setNewDuration(e.target.value)}
              min="5"
              step="5"
              className="whm-mini-input"
            />
          </div>
          <div className="whm-form-field--price">
            <label className="whm-form-label">Preço R$</label>
            <input
              type="number"
              value={newPrice}
              onChange={(e) => setNewPrice(e.target.value)}
              min="0"
              step="0.01"
              placeholder="0.00"
              className="whm-mini-input"
            />
          </div>
          <button
            type="button"
            onClick={addService}
            disabled={!newName.trim()}
            className={`whm-add-btn ${newName.trim() ? 'whm-add-btn--active' : 'whm-add-btn--disabled'}`}
          >
            <IconPlus size={13} /> Adicionar
          </button>
        </div>

        {/* ── Slot do agente nesta agenda ── */}
        <div className="whm-agent-slot">
          <div className="whm-agent-slot__header">
            <div className="whm-agent-slot__label">
              <IconRobot size={14} style={{ color: '#10b981' }} />
              Slot do agente nesta agenda
            </div>
            <label className="whm-toggle" title={agentSlotEnabled ? 'Slot fixo ativo' : 'Modo livre — desligado'}>
              <input
                type="checkbox"
                checked={agentSlotEnabled}
                onChange={() => setAgentSlotEnabled((v) => !v)}
              />
              <span className="whm-toggle-slider" />
            </label>
          </div>
          {agentSlotEnabled ? (
            <>
              <div className="whm-agent-slot__row">
                <input
                  type="number"
                  min="1"
                  step="5"
                  value={agentSlotDuration}
                  onChange={(e) => setAgentSlotDuration(e.target.value)}
                  placeholder={`Usar padrão global (${globalSlotDuration}min)`}
                  className="whm-mini-input whm-agent-slot__input"
                />
                <span className="whm-agent-slot__unit">min</span>
              </div>
              <p className="whm-agent-slot__hint">
                Intervalo dos horários que o agente sugere ao cliente. Deixe vazio para usar
                o padrão global ({globalSlotDuration}min). Não afeta a grade visual do calendário.
              </p>
            </>
          ) : (
            <p className="whm-agent-slot__hint whm-agent-slot__hint--free">
              <strong>Modo livre.</strong> O agente sugere horários conforme a duração de cada serviço escolhido pelo cliente. Útil quando os serviços têm durações muito diferentes.
            </p>
          )}
        </div>

        <hr className="whm-divider" />

        {/* ── Horários de atendimento ── */}
        <div className="whm-section-title">
          <IconClock size={14} style={{ color: '#10b981' }} />
          Horários de atendimento
        </div>

        {/* Presets */}
        <div className="whm-presets">
          <span className="whm-presets-label">Predefinir:</span>
          <button type="button" onClick={() => setDays(makeDefault())} className="whm-preset-btn">
            Dias úteis 08-18h
          </button>
          <button
            type="button"
            onClick={() =>
              setDays(DAYS.map((d) => ({ day_of_week: d.value, is_active: true, intervals: [{ start_time: '00:00', end_time: '23:59' }] })))
            }
            className="whm-preset-btn"
          >
            24h / 7 dias
          </button>
          <button
            type="button"
            onClick={() => setDays(DAYS.map((d) => ({ day_of_week: d.value, is_active: false, intervals: [] })))}
            className="whm-preset-btn whm-preset-btn--danger"
          >
            Limpar tudo
          </button>
        </div>

        {/* Day rows */}
        {DAYS.map(({ value: dayVal, label }) => {
          const day = days.find((d) => d.day_of_week === dayVal) || { day_of_week: dayVal, is_active: false, intervals: [] };
          return (
            <div key={dayVal} className={`whm-day-row${day.is_active ? ' whm-day-row--active' : ''}`}>

              <label className="whm-day-label">
                <input
                  type="checkbox"
                  checked={day.is_active}
                  onChange={(e) => {
                    const active = e.target.checked;
                    updateDay(dayVal, {
                      is_active: active,
                      intervals: active && day.intervals.length === 0 ? [{ start_time: '08:00', end_time: '18:00' }] : day.intervals,
                    });
                  }}
                  className="whm-day-checkbox"
                />
                {label}
              </label>

              <div className="whm-day-intervals">
                {day.is_active ? (
                  <>
                    {day.intervals.map((iv, ivIdx) => (
                      <div key={ivIdx} className="whm-interval-row">
                        <input
                          type="time"
                          value={iv.start_time}
                          onChange={(e) => updateInterval(dayVal, ivIdx, 'start_time', e.target.value)}
                          className="whm-time-input"
                        />
                        <span className="whm-interval-sep">–</span>
                        <input
                          type="time"
                          value={iv.end_time}
                          onChange={(e) => updateInterval(dayVal, ivIdx, 'end_time', e.target.value)}
                          className="whm-time-input"
                        />
                        <button
                          type="button"
                          onClick={() => removeInterval(dayVal, ivIdx)}
                          className="whm-icon-btn whm-icon-btn--danger"
                          title="Remover"
                        >
                          <IconTrash size={13} />
                        </button>
                      </div>
                    ))}
                    <button
                      type="button"
                      onClick={() => addInterval(dayVal)}
                      className="whm-icon-btn whm-icon-btn--success whm-add-interval-btn"
                    >
                      <IconPlus size={12} /> intervalo
                    </button>
                  </>
                ) : (
                  <span className="whm-day-closed">Fechado</span>
                )}
              </div>
            </div>
          );
        })}

        {saveError && (
          <div className="whm-error">
            <IconAlertCircle size={14} />
            {saveError}
          </div>
        )}
      </Modal.Body>

      <Modal.Footer className="whm-footer">
        <button
          type="button"
          onClick={onClose}
          disabled={saving}
          className="whm-cancel-btn"
        >
          Cancelar
        </button>
        <button
          type="button"
          onClick={handleSave}
          disabled={saving}
          className={`whm-save-btn${saving ? ' whm-save-btn--saving' : ''}`}
        >
          {saving ? (
            <><Spinner animation="border" size="sm" /> Salvando...</>
          ) : (
            <><IconCheck size={14} /> Salvar</>
          )}
        </button>
      </Modal.Footer>
    </Modal>

    <ServiceAgentVisibilityGuideModal
      show={showGuide}
      onHide={() => setShowGuide(false)}
    />
    </>
  );
};

export default WorkingHoursModal;
